import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

import Password from 'assets/img/password.svg';
import Lock from 'assets/img/lock.svg';
import Empty from 'assets/img/empty.svg';

import Button from 'components/button';
import Footer from 'components/footer';
import Header from 'components/header';
import MetaTags from 'components/meta-tags';
import FileRow from 'components/file-row';
import Spacer from 'components/spacer';
import InputContainer from 'components/input-container';
import SearchField from 'components/search-field';
import Socials from 'components/socials';
import Features from 'components/features';
import Loader from 'components/loader';

import If from 'conditionals/if';

import Error from './error';

import LocaleContext from 'contexts/locale-context';
import NotificationContext from 'contexts/notification-context';

import getJsonFromSearch from 'helpers/get-json-from-search';
import getAuthHeader from 'helpers/get-auth-header';
import doScrolling from 'helpers/do-scrolling';

import fetchFiles from 'actions/fetch-files';
import downloadFile from 'actions/download-file';
import bulkDownload from 'actions/bulk-download';
import getFeatures from 'actions/get-features';

import './index.css';

const PASSWORD_REQUIRED_STATUS = 401;
const TRANSFER_EXPIRED_STATUS = 410;
const OK_STATUS = 200;
const INVALID_ID_STATUS = 400;

const onDownload = (transferId, objectId, password) => () => {
  downloadFile(transferId, objectId, getAuthHeader(password && password.pass ? password.pass : password));
}

const formatExpiry = expiry => {
  if (expiry) {
    const date1 = new Date(expiry);
    const date2 = new Date();
      
    // To calculate the time difference of two dates 
    const Difference_In_Time = date1.getTime() - date2.getTime(); 
      
    // To calculate the no. of days between two dates 
    const Difference_In_Days = Difference_In_Time / (1000 * 3600 * 24);

    return Math.floor(Difference_In_Days).toFixed(0);
  }
}

const Download = () => {
  const location = useLocation();
  const query = getJsonFromSearch(location.search);

  const id = query.id;

  const { translations } = useContext(LocaleContext);
  const { setNotification } = useContext(NotificationContext);
  
  const [password, setPassword] = useState(null);
  const [listings, setListings] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [expiry, setExpiry] = useState(null);
  const [filter, setFilter] = useState('');
  const [blinkBack, setBlinkBack] = useState(false);
  const [passwordRequiredLoad, setPasswordRequiredLoad] = useState(false);

  let listingsToShow = listings;

  const passwordInputRef = useRef(null);
  const pageTranslations = translations.download;
  const jsTranslations = translations.js

  const hasId = !!id;
  const hasFiles = listings.length > 0;
  const showDownloadAll = listings.length > 10;

  const isExpired = status === TRANSFER_EXPIRED_STATUS;
  const isPasswordRequired = status === PASSWORD_REQUIRED_STATUS;
  const isIdInvalid = status === INVALID_ID_STATUS;

  const isError = (isExpired || isIdInvalid) && !hasFiles;

  const centerBody = isError || isPasswordRequired || loading;

  const featuresWhiteList = [
    'EXPORT_TO_GOOGLE_DRIVE',
    'EXPORT_TO_DROPBOX'
  ];

  useEffect(() => {
    const getFeaturesFromAPI = async () => {
      const fetchedFeatures = await getFeatures();
      setFeatures(fetchedFeatures.filter(feature => featuresWhiteList.indexOf(feature.name) !== -1));
    }

    getFeaturesFromAPI();
  }, []);

  useEffect(() => {
   const fetchFilesProxy = async (id, password) => {
     if (password) {
      setPasswordRequiredLoad(true)
      setLoading(false)
     } else {
      setLoading(true);
      setPasswordRequiredLoad(false)
     }

     try {
      const res = await fetchFiles(id, password && password.pass);

      if (res && res.status === OK_STATUS && res.result) {
        setListings(res.result.listing);
        setExpiry(res.result.info.expiry)
      } else {
        if (password) {
          if (res.status !== TRANSFER_EXPIRED_STATUS && res.status !== INVALID_ID_STATUS) {
            setNotification({
              message: jsTranslations.invalid_password,
              type: 'danger',
              autoCancel: true
            })
          } else {

          }
        }
      }

      setStatus(res.status);
      setTimeout(() => setLoading(false), 400)
      setTimeout(() => setPasswordRequiredLoad(false), 400);
     } catch (e) {
       setPasswordRequiredLoad(false)
       setLoading(false);
     }
   }

   fetchFilesProxy(id, password);
  }, [id, password]);

  if (filter) {
    listingsToShow = listings.filter(listing => {
      const reg = new RegExp(`.*${filter}.*`, "i");
  
      return reg.test(listing.object_name);
    })
  }

  const onBulkDownload = (setBulkLoading, transferId, password) => async () => {
    setBulkLoading(true);

    let authHeader = {};
    if (password) {
      authHeader = getAuthHeader(password && password.pass ? password.pass : password);
    }

    try {
      await bulkDownload(transferId, authHeader).process();
    } catch(e){
      setNotification({
        message: jsTranslations.something_went_wrong,
        type: 'danger',
        autoCancel: true
      })
    }

    setBulkLoading(false);
  }

  const onJumpToDownload = () => {
    doScrolling('#download', 500).then(blink);
  }

  const blink = () => {
    setBlinkBack(true);
    setTimeout(() => setBlinkBack(false), 1000)
  }

  return (
    <div className='download__container'>
      <MetaTags />
      <Header>
        <If condition={!isExpired && !isIdInvalid && !isPasswordRequired && !loading && hasFiles}>
          <Button onClick={onJumpToDownload}>
            {jsTranslations.view_download}
          </Button>
        </If>
      </Header>
      <div className='container'>
        <div className={`download__body${centerBody ? ' download__body-center' : ''}`}>
          <If condition={loading && !passwordRequiredLoad}>
            <div className='download__loader'>
              <Loader intent='primary'/>
            </div>
          </If>

          <If condition={(isPasswordRequired && !loading) || (passwordRequiredLoad && !loading) }>
            <div className='download__password'>
              <Spacer size='5xl'/>

              <If condition={isMobile}>
                <img alt='lock icon' className='download__password__icon' src={Lock} />
              </If>

              <If condition={!isMobile}>
                <img alt='password icon' className='download__password__icon' src={Password} />
              </If>

              <h1>{pageTranslations.password_protected}</h1>

              <Spacer size='m'/>

              <p>
                {pageTranslations.transfer_protected}
              </p>

              <p>
                {pageTranslations.dont_have_password}
              </p>

            <form
              onSubmit={e => {
                e.preventDefault();
                setPassword({ pass: passwordInputRef.current.value })
              }}
            >
              <div className='download__password__input'>
                <InputContainer>
                  {({ class: className }) => {
                    return <input
                      type='password'
                      ref={passwordInputRef}
                      className={className}
                      placeholder={pageTranslations.password}
                      required
                    />
                  }}
                </InputContainer>
                <Spacer horizontal size='m' />
                <Button type='submit' loading={passwordRequiredLoad}>
                  {pageTranslations.unlock}
                </Button>
              </div>
            </form>
            </div>
          </If>

          <If condition={!loading && !passwordRequiredLoad && hasFiles}>
            <Spacer size='xl' />

            <Features
              features={features}
              transferId={id}
              password={password || null}
            />
            <Spacer size='xxxxl' />

            <div id='download' className={`download__files__container ${blinkBack ? `download__files__container--blink` : ''}`}>
              <Spacer size='xxxl' />

              <If condition={!!expiry}>
                <p className='download__files__title'>
                  {formatExpiry(expiry) === 1 ?
                    `${pageTranslations.expires_in_day.replace('{NUM}', formatExpiry(expiry))}`:
                    `${pageTranslations.expires_in_days.replace('{NUM}', formatExpiry(expiry))}`
                  }
                </p>
              </If>

              <Spacer size='m' />

              <Socials downloadLink={window.location.href} transferId={id} password={password}/>

              <Spacer size='xxl' />

              <div className={`download__files`}>
                <div className='download__files__search width-100 flex row'>
                  <SearchField
                    onChange={e => setFilter(e.target.value)}
                  />                

                  <If condition={showDownloadAll}>
                    <div>
                      <Spacer horizontal size='s' />
                      <Button
                        btnType='none'
                        loading={bulkLoading}
                        onClick={onBulkDownload(setBulkLoading, id, password)}
                      >
                        {pageTranslations.download_all}
                      </Button>
                    </div>
                  </If>
                </div>

                <Spacer size='m' />

                <div className='download__files__listings'>
                  {listingsToShow.map(
                    (listing, index) => 
                      <FileRow
                        key={index}
                        listing={listing}
                        onDownload={onDownload(id, listing.object_id, password)}
                      />
                  )}
                </div>
              </div>
            </div>
          </If>

          <If condition={(!hasId || isIdInvalid || isExpired ) && !loading}>
            <Spacer size='5xl' />

            <Error
              title={pageTranslations.cant_find_transfer}
              subtitle={pageTranslations.incorrect_url}
              image={Empty}
            />
          </If>
          
        </div>
        <Footer suffix={`download${location.search}`}/>
      </div>
    </div>
  );
}

export default Download;
